import { Breadcrumbs, Heading, ParagraphSkeleton, Skeleton, SkeletonGroup } from "@r360/library";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { requestLoadingSelector, resvTypeByLid } from "../../Selectors";
import { AccommodationExtras } from "../../components/AccommodationExtras/AccommodationExtras";
import PageContainer from "../../components/PageContainer";
import useAppSelector from "../../hooks/useAppSelector";
import { useDynamicParams } from "../../hooks/useDynamicParams";
import { usePathBuilder } from "../../hooks/usePathBuilder";
import useTranslate from "../../hooks/useTranslate";
import { reservationResultSelector } from "../../selectors/Selectors";
import { TCartItem, TItem } from "../../store/types";

const AccommodationExtrasPage = () => {
    const t = useTranslate();
    const { buildPath } = usePathBuilder();
    const location = useLocation();
    const { reservationTypeId, grouppoollid, poollid, unitlid, id } = useDynamicParams();
    const reservationResult = useAppSelector(reservationResultSelector);
    const accommodationResult = reservationTypeId ? reservationResult[reservationTypeId] : undefined;
    const cartProducts: TCartItem[] = useAppSelector(state => state.checkoutCart?.products);
    const reservationType = useAppSelector(state => resvTypeByLid(state, reservationTypeId));
    const reservationTypeDescription = reservationType?.description;
    const { isMobile } = useAppSelector(state => state.window);
    const fetchingExtrasInProgress = useAppSelector(state => requestLoadingSelector(state, "fetchProductsExtras"));
    const [item, setItem] = useState<TItem | TCartItem>();
    const [isCartItem, setIsCartItem] = useState(false);

    useEffect(() => {
        // Try to find the item in the cart, otherwise in the searchresult.
        const cartItem = cartProducts.find(
            product =>
                Number(product.id) === Number(id) || // First, try matching with id.
                Number(product.original_id) === Number(id) // Then, try matching with original_id, if it´s an existing reservation.
        );
        if (cartItem) {
            setItem(cartItem);
            setIsCartItem(true);
        } else if (!fetchingExtrasInProgress) {
            const item = Object.values(accommodationResult?.groups ?? {})
                .find(group => group.grouppoollid === grouppoollid)
                ?.items.find(item => item.id === id);

            // TODO Redirect if item is not found. Use buildPath.

            setItem(item);
            setIsCartItem(false);
        }
    }, [accommodationResult, cartProducts, grouppoollid, id, fetchingExtrasInProgress]);

    const renderBreadcrumbs = () => {
        if (!reservationTypeId) {
            return null;
        }

        const breadcrumbs = [
            {
                title: reservationTypeDescription,
                path: `${buildPath(reservationTypeId)}${location.search}`,
            },
            {
                title: item?.groupTitle ?? "",
                path: `${buildPath(reservationTypeId, grouppoollid)}${location.search}`,
            },
            {
                title: item?.title ?? "",
                path: `${buildPath(reservationTypeId, grouppoollid, poollid, unitlid)}${location.search}`,
            },
            {
                title: t("book.accomodation.extras"),
                path: "",
            },
        ];

        return <Breadcrumbs breadcrumbs={breadcrumbs} firstItemsDropdown={isMobile} />;
    };

    return (
        <PageContainer>
            {fetchingExtrasInProgress && (
                <section className="u-pt-24">
                    <Heading type="h1" className="u-mb-48">
                        {t("book.accomodation.extras.heading")}
                    </Heading>
                    <SkeletonGroup>
                        <div className="u-d-flex u-flex-column u-gap-24">
                            {[...Array(5)].map((_, index) => {
                                return (
                                    <Row key={index}>
                                        <Col xs={6}>
                                            <ParagraphSkeleton />
                                        </Col>
                                        <Col xs={2} className="u-d-flex u-justify-content-end">
                                            <Skeleton />
                                        </Col>
                                        <Col xs={2} className="u-d-flex u-justify-content-end">
                                            <Skeleton />
                                        </Col>
                                        <Col xs={2} className="u-d-flex u-justify-content-end">
                                            <Skeleton />
                                        </Col>
                                    </Row>
                                );
                            })}
                        </div>
                    </SkeletonGroup>
                </section>
            )}
            {!fetchingExtrasInProgress && item && (
                <>
                    {!isCartItem && renderBreadcrumbs()}
                    <AccommodationExtras item={item} groupTitle={item.title} loadingExtras={fetchingExtrasInProgress} />
                </>
            )}
        </PageContainer>
    );
};

export default AccommodationExtrasPage;
